<template>
  <p-secure :require-role="[SECURITYROLES.STOREMANAGER]">
    <p-search-screen
        title="Transaction Search"
        api-destination="transactions"
        :filters="filters"
        :filterBy="filterBy"
        :fields="fields"
        :component-is-loading="componentIsLoading"
        >
        <template v-slot:actionButtons>
            <p-button
                variant="outline-primary"
                v-on:click="printScreen()"
                >Print</p-button
            >
            <b-overlay :show="isExporting"
                                   rounded
                                   opacity="0.5"
                                   spinner-small
                                   spinner-variant="primary"
                                   class="d-inline-block">
                            <b-dropdown text="Export" variant="outline-primary">
                                <b-dropdown-item @click="exportTransactions('csv')" >Export Summary (CSV)</b-dropdown-item>
                                <b-dropdown-item @click="exportTransactions('pdf')" >Export Details (PDF)</b-dropdown-item>
                                <b-dropdown-item @click="exportTransactions('detailsCsv')" >Export Details (CSV)</b-dropdown-item>
                                <b-dropdown-item @click="exportTransactions('dat')">Colleague (DAT)</b-dropdown-item>
                            </b-dropdown>
                        </b-overlay>
        </template>
        <template v-slot:belowSearch>
            <p-form>
                <b-row>
                    <b-col>
                        <p-select
                            label="POS Device"
                            data-type="posDevicesWithLocation"
                            v-model="filters.posDeviceId"
                            noSelectionMessage="Show All"
                        />
                    </b-col>
                    <b-col>
                        <p-select
                            label="Cashier"
                            data-type="nbcUsers"
                            v-model="filters.cashierId"
                            noSelectionMessage="Show All"
                        />
                    </b-col>
                    <b-col>
                        <p-select
                            label="Transaction Type"
                            :options="transactionTypeOptions"
                            v-model="filters.transactionType"
                            noSelectionMessage="Show All"
                        />
                    </b-col>
                    <b-col>
                        <p-select
                            label="Tender"
                            data-type="tenders"
                            v-model="filters.tenderId"
                            noSelectionMessage="Show All"
                        />
                    </b-col>
                    <b-col>
                        <p-select
                            label="AR Account / Agency"
                            data-type="accounts"
                            v-model="filters.accountId"
                            noSelectionMessage="Show All"
                        />
                    </b-col>
                </b-row>
                <b-row class="pt-2">
                    <b-col>
                            <p-date-range :date-range-options="filters" @date-picker-initialized="datePickerInitialized" />
                    </b-col>
                </b-row>
            </p-form>
        </template>
        <template v-slot:cell(view)="{ value, item }">
            <b-button variant="outline-primary" size="sm" @click='getTransaction(item)' v-b-modal.customerTransactionModal>View</b-button>
        </template>
    </p-search-screen>
    <TransactionDetailModal :transaction="selectedTransaction" @closed="cancelGetTransaction" />
    </p-secure>
</template>

<script>
    import axios from 'axios';
    import { datetimeToLocalTimeFormatter, currencyFormatter } from '../../components/Common/Formatters.js';
    import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
    import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
    import TransactionDetailModal from '../../components/TransactionDetailModal.vue';

    export default {
        data() {
            return {
                isExporting: false,
                selectedTransaction: null,
                componentIsLoading: true,
                filters: {
                    automatic: "",
                    perPage: 100,
                    sortBy: 'transactionId',
                    sortDesc: true,
                    searchBy:'All',
                    fromDate: null,
                    toDate: null,
                    posDeviceId: null,
                    cashierId: null,
                    transactionType: null,
                    tenderId: null,
                    accountId: null
                },
                filterBy:[
                    'All',
                    'Transaction ID',
                    'Last Name',
                    'First Name',
                    'Customer Account #',
                    'Serial #',
                    'Dollar Amount',
                    'Variant',
                    'SKU',
                    'Barcode',
                    'Description'
                ],
                transactionTypeOptions: [
                    { text: 'Pay In', value: 'Pay In' },
                    { text: 'Pay Out', value: 'Pay Out' },
                    { text: 'Refund', value: 'Refund' },
                    { text: 'Sale', value: 'Sale' },
                    { text: 'Rental Check In', value: 'Rental Check In' },
                    { text: 'Rented', value: 'Rented' }
                ],
                fields: [
                    {
                        key: 'view',
                        label: ''
                    },
                    {
                        key: 'transactionId',
                        label: 'Transaction ID',
                        sortable: true
                    },
                    {
                        key: 'lastName',
                        label: 'Last Name',
                        sortable: true
                    },
                    {
                        key: 'firstName',
                        label: 'First Name',
                        sortable: true
                    },
                    {
                        key: 'studentId',
                        label: 'Student ID',
                        sortable: true
                    },
                    {
                        key: 'transactionDate',
                        label:'Date & Time',
                        sortable: true,
                        formatter: datetimeToLocalTimeFormatter
                    },
                    {
                        key: 'posDeviceName',
                        label: 'POS Device',
                        sortable: true
                    },
                    {
                        key: 'cashier',
                        label: 'Cashier',
                        sortable: true
                    },
                    {
                        key: 'tender',
                        label: 'Tender'
                    },
                    {
                        key: 'arAccountCode',
                        label: 'AR Code'
                    },
                    {
                        key: 'arAgencyName',
                        label: 'AR Agency',
                        formatter: (value) => {
                            return value?.substr(0,15);
                        },
                    },
                    {
                        key: 'transactionType',
                        label: 'Transaction Type',
                        formatter: (value, key, item) => {
                            let transactionTypes = [];

                            if (item.hasPayIn)
                            {
                                transactionTypes.push("Pay In");
                            }

                            if (item.hasPayOut)
                            {
                                transactionTypes.push("Pay Out");
                            }

                            if (item.hasRefund)
                            {
                                transactionTypes.push("Refund");
                            }

                            if (item.hasSale)
                            {
                                transactionTypes.push("Sale");
                            }

                            if (item.hasCheckIn)
                            {
                                transactionTypes.push("Rental Check In");
                            }

                            if (item.hasRented)
                            {
                                transactionTypes.push("Rented");
                            }

                            return transactionTypes.join(', ');
                        },

                        sortable: true
                    },
                    {
                        key: 'transactionTotal',
                        label: 'Total',
                        sortable: true,
                        formatter: currencyFormatter,
                        thClass: 'text-center',
                        tdClass: 'text-end'
                    }
                ]
            };
        },
        methods: {
            datePickerInitialized(){
                this.componentIsLoading = false;
            },
            printScreen()
            {
                window.print();
            },
            getTransaction(transaction) {
                // Cancel any earlier in-flight fetch so a stale response can't
                // populate the modal after the user already moved on.
                this.cancelGetTransaction();
                this.selectedTransaction = null;
                this.transactionAbortController = new AbortController();
                const controller = this.transactionAbortController;
                axios.get("customers/getTransaction", {
                        params: { saleFulfillmentId: transaction.saleFulfillmentId },
                        signal: controller.signal
                    })
                    .then(resp => {
                        if (controller.signal.aborted) return;
                        this.selectedTransaction = resp.data;
                        this.$bvModal.show("customerTransactionModal");
                    })
                    .catch(err => {
                        if (axios.isCancel?.(err) || err?.name === 'CanceledError') return;
                        throw err;
                    });
            },
            cancelGetTransaction() {
                this.transactionAbortController?.abort();
                this.transactionAbortController = null;
            },
            async exportTransactions(type) {
                this.isExporting = true;

                if (type == 'csv' || type == 'detailsCsv') {
                    let apiUrl = '/transactions/exporttransactions';
                    this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                    axios
                        .post(
                            apiUrl,
                            {
                                automatic: this.filters.automatic,
                                searchBy: this.filters.searchBy,
                                locationId: await selectListOptionsDataContext.getSelectedLocationIdAsync(),
                                fromDate: this.filters.fromDate,
                                toDate: this.filters.toDate,
                                posDeviceId: this.filters.posDeviceId ?? 0,
                                cashierId: this.filters.cashierId ?? 0,
                                transactionType: this.filters.transactionType,
                                tenderId: this.filters.tenderId,
                                sortBy: this.filters.sortBy,
                                sortDesc: this.filters.sortDesc,
                                accountId: this.filters.accountId,
                                isExportDetailsCsv:(type == 'detailsCsv'?true:false)
                            },
                            { responseType: 'blob' }
                        )
                        .then(result => {
                            downloadFileToBrowser(
                                result.data,
                                `Transaction Search.csv`,
                                'application/csv'
                            );
                            this.$appSuccess(
                                    `Transaction Search report generated successfully.`
                                )
                        })
                        .finally(() => {
                            this.isExporting = false;
                        });
                }

                if (type == 'pdf') {
                    let apiUrl='/transactions/exportpdftransactions';
                    this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                    this.isExporting = true;
                    axios
                        .post(
                            apiUrl,
                            {
                                automatic: this.filters.automatic,
                                searchBy: this.filters.searchBy,
                                locationId: await selectListOptionsDataContext.getSelectedLocationIdAsync(),
                                fromDate: this.filters.fromDate,
                                toDate: this.filters.toDate,
                                posDeviceId: this.filters.posDeviceId ?? 0,
                                cashierId: this.filters.cashierId ?? 0,
                                transactionType: this.filters.transactionType,
                                tenderId: this.filters.tenderId,
                                sortBy: this.filters.sortBy,
                                sortDesc: this.filters.sortDesc,
                                accountId: this.filters.accountId
                            },
                            { responseType: 'blob' }
                        )
                        .then(result => {
                            downloadFileToBrowser(
                                result.data,
                                `TransactionDetails`,
                                'application/pdf'
                            );
                            this.$appSuccess(`Transaction Search report generated successfully.`);
                        })
                        .finally(() => {
                            this.isExporting = false;
                        });
                }
                
                if (type == 'dat') {
                    this.filters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
                    this.isExporting = true;
                    axios
                        .post(
                            '/transactions/exportcolleaguedattransactions',
                            {
                                automatic: this.filters.automatic,
                                searchBy: this.filters.searchBy,
                                locationId: await selectListOptionsDataContext.getSelectedLocationIdAsync(),
                                fromDate: this.filters.fromDate,
                                toDate: this.filters.toDate,
                                posDeviceId: this.filters.posDeviceId ?? 0,
                                cashierId: this.filters.cashierId ?? 0,
                                transactionType: this.filters.transactionType,
                                tenderId: this.filters.tenderId,
                                sortBy: this.filters.sortBy,
                                sortDesc: this.filters.sortDesc,
                                accountId: this.filters.accountId
                            },
                            { responseType: 'blob' }
                        )
                        .then(result => {
                            downloadFileToBrowser(
                                result.data,
                                `Colleague.dat`,
                                'application/octet-stream');
                            this.$appSuccess(`Transaction Search report generated successfully.`);
                        })
                        .finally(() => {
                            this.isExporting = false;
                        });
                }                
            }
        },
        components: {
            TransactionDetailModal
        }
    };
</script>
<style scoped>
</style>